import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import {
  TransitionDirection,
  TransitionDto,
  TransitionSpeed,
  TransitionKind,
} from "@repo/api/transition";
import { TransitionFormConfig } from "@repo/video-engine-i2v/components/ImageTransitionBuilder/ImageTransitionBuilder";
import {
  LIGHT_LEAK_DEFAULT_INTENSITY,
  LIGHT_LEAK_DEFAULT_SEED,
} from "@repo/video-engine-i2v/components/ImageTransitionBuilder/ImageLightLeakTransitionBuilder";
import {
  TransitionIntensityControl,
  TransitionPatternControl,
} from "./LightLeakControls";
import { TransitionPreview, UseTransitionPreview } from "./TransitionPreview";
import { useCallback, useRef, useState } from "react";
import { PlayerRef } from "@remotion/player";
import TransitionListItem from "./TransitionListItem";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/lib/components/Button/button-variants";
import {
  IconArrowLeft,
  IconChevronRight,
  IconArrowRight,
  IconArrowDown,
  IconArrowUp,
  IconArrowDownRight,
  IconArrowDownLeft,
  IconArrowUpLeft,
  IconArrowUpRight,
} from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";

type Props = {
  isOpen: boolean;
  currentTransition: TransitionDto;
  transitionFormConfigs: TransitionFormConfig[];
  noneKindLabel?: string;

  onOpenChange: (isOpen: boolean) => void;
  onTransitionChange: (transition: TransitionDto) => void;
  onTransitionChangeAll?: (transition: TransitionDto) => void;
  useTransitionPreview: UseTransitionPreview;
};

const transitionOptions: {
  value: TransitionKind;
  label: string;
}[] = [
  { value: "NONE", label: "No Transition" },
  { value: "FADE", label: "Fade" },
  { value: "FADE_BLACK", label: "Fade Black" },
  { value: "FADE_WHITE", label: "Fade White" },
  { value: "PUSH", label: "Push" },
  { value: "WIPE", label: "Wipe" },
  { value: "ZOOM_IN_FADE", label: "Zoom & Fade" },
  { value: "ZOOM_IN_OUT", label: "Zoom In & Out" },
  { value: "ZOOM_BLUR", label: "Zoom Blur" },
  { value: "ZOOM_OUT_BLUR", label: "Zoom Out Blur" },
  { value: "GLITCH_CUT", label: "Glitch Cut" },
  { value: "LIGHT_LEAK", label: "Light Leak" },
  { value: "FOCUS_PULL", label: "Focus Pull" },
  { value: "WHIP_PAN", label: "Whip Pan" },
  { value: "CLOCK_WIPE", label: "Clock Wipe" },
  { value: "SPLIT_REVEAL", label: "Split Reveal" },
  { value: "SPLIT_SCREEN_WIPE", label: "Split Screen" },
];

const speedOptions: {
  value: TransitionSpeed;
  label: string;
}[] = [
  { value: "SLOW", label: "Slow" },
  { value: "NORMAL", label: "Normal" },
  { value: "FAST", label: "Fast" },
];

const directionOptions: {
  value: TransitionDirection;
  Icon: React.ComponentType;
}[] = [
  { value: "FROM_LEFT", Icon: IconArrowRight },
  { value: "FROM_RIGHT", Icon: IconArrowLeft },
  { value: "FROM_TOP", Icon: IconArrowDown },
  { value: "FROM_BOTTOM", Icon: IconArrowUp },
  { value: "FROM_TOP_LEFT", Icon: IconArrowDownRight },
  { value: "FROM_TOP_RIGHT", Icon: IconArrowDownLeft },
  { value: "FROM_BOTTOM_LEFT", Icon: IconArrowUpRight },
  { value: "FROM_BOTTOM_RIGHT", Icon: IconArrowUpLeft },
];

export type TransitionDialogView = "TRANSITION_LIST" | "TRANSITION_CONFIG";

export default function TransitionDialog({
  isOpen,
  onOpenChange,
  currentTransition,
  onTransitionChange,
  onTransitionChangeAll,
  transitionFormConfigs,
  useTransitionPreview,
  noneKindLabel,
}: Props) {
  const playerRef = useRef<PlayerRef | null>(null);
  const [view, setView] = useState<TransitionDialogView>(
    currentTransition.kind === "NONE" ? "TRANSITION_LIST" : "TRANSITION_CONFIG",
  );

  const [previewTransition, setPreviewTransition] =
    useState<TransitionDto>(currentTransition);

  const playPreview = useCallback(
    (transition: TransitionDto) => {
      playerRef.current?.seekTo(0);
      setPreviewTransition(transition);
      playerRef.current?.play();
    },
    [playerRef],
  );

  const pausePreview = useCallback(() => {
    playerRef.current?.seekTo(0);
    playerRef.current?.pause();
    setPreviewTransition(currentTransition);
  }, [playerRef, currentTransition]);

  const setTransition = useCallback(
    (transition: TransitionDto) => {
      pausePreview();
      onTransitionChange(transition);
      setPreviewTransition(transition);
      setView("TRANSITION_CONFIG");
    },
    [onTransitionChange, pausePreview],
  );

  const updateTransition = useCallback(
    (transition: TransitionDto) => {
      onTransitionChange(transition);
      setPreviewTransition(transition);
    },
    [onTransitionChange],
  );

  const currentTransitionFormConfig = transitionFormConfigs.find(
    (config) => config.kind === currentTransition.kind,
  );

  if (!currentTransitionFormConfig) {
    throw new Error(
      `Transition form config not found for kind: ${currentTransition?.kind}`,
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-134 max-h-[90svh] w-full max-w-191 overflow-hidden"
        classNameInner="flex min-h-0 flex-col gap-0 overflow-hidden p-0 sm:px-0"
        showCloseButton={false}
      >
        <DialogHeader
          variant="bordered"
          showCloseButton={true}
          className="px-8 pr-6"
        >
          <DialogTitle>
            {view === "TRANSITION_LIST" ? "Select transition" : "Transition"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Transition dialog
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 overflow-hidden">
          {view === "TRANSITION_LIST" && (
            <TransitionDialogList
              noneKindLabel={noneKindLabel}
              transitionFormConfigs={transitionFormConfigs}
              currentTransition={currentTransition}
              setTransition={setTransition}
              playPreview={playPreview}
              pausePreview={pausePreview}
            />
          )}
          {view === "TRANSITION_CONFIG" && (
            <TransitionDialogConfig
              noneKindLabel={noneKindLabel}
              currentTransitionFormConfig={currentTransitionFormConfig}
              currentTransition={currentTransition}
              setView={setView}
              updateTransition={updateTransition}
              playPreview={playPreview}
              pausePreview={pausePreview}
            />
          )}
          <TransitionPreview
            ref={playerRef}
            playerRef={playerRef}
            currentView={view}
            currentTransition={currentTransition}
            previewTransition={previewTransition}
            useTransitionPreview={useTransitionPreview}
            playPreview={playPreview}
            pausePreview={pausePreview}
          />
        </div>
        {view === "TRANSITION_CONFIG" && (
          <div className="flex shrink-0 justify-end gap-3 border-t border-border px-8 py-4">
            {onTransitionChangeAll && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onTransitionChangeAll(currentTransition);
                  onOpenChange(false);
                }}
              >
                Apply to all clips
              </Button>
            )}
            <Button size="sm" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

type ListProps = {
  noneKindLabel?: string;
  transitionFormConfigs: TransitionFormConfig[];
  currentTransition: TransitionDto;
  setTransition: (transition: TransitionDto) => void;
  playPreview: (transition: TransitionDto) => void;
  pausePreview: () => void;
};

function TransitionDialogList({
  noneKindLabel,
  transitionFormConfigs,
  currentTransition,
  setTransition,
  playPreview,
  pausePreview,
}: ListProps) {
  const allowedKinds = transitionFormConfigs.map((config) => config.kind);

  const filteredKindOptions = transitionOptions.filter((option) =>
    allowedKinds.includes(option.value),
  );

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-8 py-6">
      {filteredKindOptions.map((option) => {
        const newFormConfig = transitionFormConfigs.find(
          (config) => config.kind === option.value,
        );

        if (!newFormConfig) {
          throw new Error(
            `Transition form config not found for kind: ${option.value}`,
          );
        }

        return (
          <TransitionListItem
            key={option.value}
            isSelected={currentTransition.kind === option.value}
            onMouseEnter={() => {
              playPreview(newFormConfig.buildDefaultValue());
            }}
            onMouseLeave={() => {
              pausePreview();
            }}
            label={
              option.value === "NONE" && noneKindLabel
                ? noneKindLabel
                : option.label
            }
            onClick={() => {
              pausePreview();
              setTransition(newFormConfig.buildDefaultValue());
            }}
          />
        );
      })}
    </div>
  );
}

type ConfigProps = {
  noneKindLabel?: string;
  currentTransitionFormConfig: TransitionFormConfig;
  currentTransition: TransitionDto;
  setView: (view: TransitionDialogView) => void;
  updateTransition: (transition: TransitionDto) => void;
  playPreview: (transition: TransitionDto) => void;
  pausePreview: () => void;
};

function TransitionDialogConfig({
  noneKindLabel,
  currentTransition,
  currentTransitionFormConfig,
  setView,
  updateTransition,
  playPreview,
  pausePreview,
}: ConfigProps) {
  const kindLabel =
    transitionOptions.find(
      (option) => option.value === currentTransitionFormConfig.kind,
    )?.label ?? "Unknown transition";

  const filteredSpeedOptions = speedOptions.filter((option) =>
    (currentTransitionFormConfig.speed ?? []).includes(option.value),
  );

  const filteredDirectionOptions = directionOptions.filter((option) =>
    (currentTransitionFormConfig.direction ?? []).includes(option.value),
  );

  return (
    <div className="flex flex-1 flex-col overflow-y-auto px-8 py-6 pl-8">
      <div className="input-label">Current transition</div>

      <button
        onClick={() => {
          setView("TRANSITION_LIST");
        }}
        className={cn(
          buttonVariants({
            variant: "outline",
            size: "sm",
            className: "flex shrink-0 justify-between px-4 font-medium",
          }),
        )}
      >
        <span>
          {currentTransitionFormConfig.kind === "NONE" && noneKindLabel
            ? noneKindLabel
            : kindLabel}
        </span>
        <IconChevronRight />
      </button>

      {filteredSpeedOptions.length > 0 && (
        <div className="mt-5">
          <div className="input-label">Speed</div>
          <div className="flex gap-2">
            {filteredSpeedOptions.map((option) => {
              const isSelected = currentTransition.speed === option.value;

              const newValue = {
                ...currentTransition,
                speed: option.value,
              };

              return (
                <Button
                  key={option.value}
                  variant={isSelected ? "gradientBorder" : "outline"}
                  size="sm"
                  className="font-medium"
                  onClick={() => {
                    updateTransition(newValue);
                  }}
                  onMouseEnter={() => {
                    playPreview(newValue);
                  }}
                  onMouseLeave={() => {
                    pausePreview();
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {currentTransitionFormConfig.intensity && (
        <div className="mt-5">
          <TransitionIntensityControl
            value={currentTransition.intensity ?? LIGHT_LEAK_DEFAULT_INTENSITY}
            onChange={(intensity) =>
              updateTransition({ ...currentTransition, intensity })
            }
            onPreview={(intensity) =>
              playPreview({ ...currentTransition, intensity })
            }
            onPreviewEnd={pausePreview}
          />
        </div>
      )}

      {filteredDirectionOptions.length > 0 && (
        <div className="mt-5">
          <div className="input-label">Direction</div>
          <div className="inline-grid grid-cols-4 gap-2">
            {filteredDirectionOptions.map((option) => {
              const isSelected = currentTransition.direction === option.value;

              const newValue = {
                ...currentTransition,
                direction: option.value,
              };

              return (
                <Button
                  key={option.value}
                  variant={isSelected ? "gradientBorder" : "outline"}
                  size="iconSm"
                  className="font-medium"
                  onClick={() => {
                    updateTransition(newValue);
                  }}
                  onMouseEnter={() => {
                    playPreview(newValue);
                  }}
                  onMouseLeave={() => {
                    pausePreview();
                  }}
                >
                  <option.Icon />
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {currentTransitionFormConfig.seed && (
        <div className="mt-5">
          <TransitionPatternControl
            value={currentTransition.seed ?? LIGHT_LEAK_DEFAULT_SEED}
            onChange={(seed) =>
              updateTransition({ ...currentTransition, seed })
            }
            onPreview={(seed) => playPreview({ ...currentTransition, seed })}
            onPreviewEnd={pausePreview}
          />
        </div>
      )}
    </div>
  );
}
